/**
 * Where a wire between two cards goes.
 *
 * Shared, because a share has to look like the canvas it came from. The viewer
 * used to carry a hand-copied port of this function, faithful on the day it was
 * written and with nothing to keep it faithful after — which is the same reason
 * the card chrome was extracted (#542).
 *
 * Geometry only: no React, no DOM. The app draws these paths in one SVG over
 * the plane, the viewer draws them under its read-only cards, and both mean the
 * same picture.
 */

export interface Point {
  x: number
  y: number
}

/** A card's vertical extent — a wrapping edge needs to clear it. */
export interface Box {
  top: number
  bottom: number
}

/** How far a wrapping edge stands off the cards it goes around. */
const LANE = 28

/**
 * The line between two ports.
 *
 * Forward — the target is to the right — is a horizontal cubic: each end points
 * the way the connection flows, so the direction survives a tangle of them.
 *
 * Backward is not. A curve to a target that sits left or below cuts straight
 * across both cards and through whatever is between them, which reads as a line
 * over the work rather than between it. So it goes around: out of the source,
 * down past the lower of the two cards, back to the target's side, and in. The
 * same shape a wire takes when it has to reach behind something.
 */
export function route(from: Point, to: Point, fromBox: Box, toBox: Box): string {
  // Forward is simply "the target's input is right of the source's output".
  // A stricter threshold sent a target sitting just beside its source the long
  // way round — out, down past a tall card, and back — for a gap a curve
  // crosses in twenty pixels.
  const forward = to.x > from.x
  if (forward) {
    const dx = Math.max(40, (to.x - from.x) * 0.5)
    return `M ${from.x} ${from.y} C ${from.x + dx} ${from.y}, ${to.x - dx} ${to.y}, ${to.x} ${to.y}`
  }

  // Everything that is not forward goes round.
  //
  // A curve was tried here for cards stacked in a column, on the theory that a
  // column has nothing to go around. That is true of the *gap* and false of the
  // cards: with any real height the curve has to cross the full width of both,
  // so it was drawn straight over the answers it connects. Going round is
  // longer and never crosses anything.

  // How far outside the ports the wire steps before turning.
  //
  // It was a full lane on each side, which put the descent well clear of the
  // target card and made the arrival a long run down open space and a hard
  // elbow back in. Close in, the corner sits just off the card's edge and the
  // wire reads as arriving at the port rather than aiming near it.
  const MARGIN = 12
  const outX = from.x + MARGIN
  const inX = to.x - MARGIN
  // Where the wire runs while it is going backwards.
  //
  // Through the gap between the two cards when there is one — that is the empty
  // corridor a person would draw it through. Clearing the outside of both cards
  // was the first attempt, and it sent the wire diving under the target only to
  // climb back up into it, which looks like a mistake even though it is
  // geometrically fine.
  //
  // The corridor qualifies as soon as it is worth using — a stacked pair with
  // an ordinary gap between them was failing a `LANE * 2` test and being sent
  // down the outside of both cards instead, which is the long sweep that made
  // two blocks in a column look like they were wired around the back.
  const CORRIDOR_MIN = 18
  const gapBelowSource = toBox.top - fromBox.bottom
  const gapAboveSource = fromBox.top - toBox.bottom
  const laneY =
    gapBelowSource > CORRIDOR_MIN
      ? fromBox.bottom + gapBelowSource / 2
      : gapAboveSource > CORRIDOR_MIN
        ? toBox.bottom + gapAboveSource / 2
        : to.y >= from.y
          ? Math.max(fromBox.bottom, toBox.bottom) + LANE
          : Math.min(fromBox.top, toBox.top) - LANE
  // Two smooth bends instead of four corners.
  //
  // The orthogonal version was right about where the wire should go and wrong
  // about how it should get there: at these distances the rounded corners read
  // as four separate segments, and the pair of right angles at the far end —
  // down the outside of the target, then a hard turn into the port — is the
  // part that kept looking broken. One curve out of the source into the
  // corridor, one out of the corridor into the port. Same route, one line.
  const midX = (outX + inX) / 2
  return [
    `M ${from.x} ${from.y}`,
    `C ${outX} ${from.y}, ${outX} ${laneY}, ${midX} ${laneY}`,
    `C ${inX} ${laneY}, ${inX} ${to.y}, ${to.x} ${to.y}`,
  ].join(' ')
}
